import { useQuery } from "@tanstack/react-query";
import { getItemsFromCollection } from "directus/src/operations";
import { useCurrencies } from "@/modules/reports/contexts/currency-context";
import { startOfMonth, endOfMonth, subMonths } from "date-fns";
import { formatDateForAPI } from "utils/utils";

export const useMoMGrowth = (jobType, filters = {}) => {
  const { convertToINR } = useCurrencies();

  return useQuery({
    queryKey: ["mom-growth", jobType, filters],
    queryFn: async () => {
      // Use date from filters if available, otherwise use current date
      let referenceDate = new Date();
      if (filters?.jobs?.billables?.collected_date?._gte) {
        referenceDate = new Date(filters.jobs.billables.collected_date._gte);
      }

      const currentMonthStart = startOfMonth(referenceDate);
      const currentMonthEnd = endOfMonth(referenceDate);
      const lastMonthStart = startOfMonth(subMonths(referenceDate, 1));
      const lastMonthEnd = endOfMonth(subMonths(referenceDate, 1));

      const baseFilter = {
        status: { _eq: "collected" },
        type: { _eq: jobType },
        job: {
          type: { _eq: jobType },
          ...filters.job,

          ...(filters?.jobs?.employees?.employee?.department?._in
            ? {
                employees: {
                  employee: {
                    department: {
                      _in: filters.jobs.employees.employee.department._in,
                    },
                  },
                },
              }
            : {}),
        },
        ...(filters?.sales_coordinator
          ? {
              job: {
                project: {
                  sales_coordinator: { _eq: filters.sales_coordinator._eq },
                },
              },
            }
          : {}),
        ...(filters?.client
          ? { job: { project: { client: { _eq: filters.client._eq } } } }
          : {}),
        ...filters.billable,
      };

      const currentMonthQuery = {
        fields: ["amount", "adjusted_amount", "currency"],
        filter: {
          ...baseFilter,
          collected_date: {
            _gte: formatDateForAPI(currentMonthStart),
            _lte: formatDateForAPI(currentMonthEnd),
          },
        },
        limit: -1,
      };

      // Last month collected billables
      const lastMonthQuery = {
        fields: ["amount", "adjusted_amount", "currency"],
        filter: {
          ...baseFilter,
          collected_date: {
            _gte: formatDateForAPI(lastMonthStart),
            _lte: formatDateForAPI(lastMonthEnd),
          },
        },
        limit: -1,
      };

      // Execute both queries
      const [currentMonthResponse, lastMonthResponse] = await Promise.all([
        getItemsFromCollection("billables", currentMonthQuery),
        getItemsFromCollection("billables", lastMonthQuery),
      ]);

      // Calculate current month amount
      const currentMonthAmount = currentMonthResponse.reduce(
        (total, billable) => {
          const amount = billable.adjusted_amount || billable.amount || 0;
          return total + convertToINR(amount, billable.currency);
        },
        0
      );

      // Calculate last month amount
      const lastMonthAmount = lastMonthResponse.reduce((total, billable) => {
        const amount = billable.adjusted_amount || billable.amount || 0;
        return total + convertToINR(amount, billable.currency);
      }, 0);

      // Calculate growth
      const momGrowthAmount = currentMonthAmount - lastMonthAmount;
      const momGrowthPercentage =
        lastMonthAmount > 0
          ? ((currentMonthAmount - lastMonthAmount) / lastMonthAmount) * 100
          : currentMonthAmount > 0
          ? 100
          : 0;

      return {
        momGrowthAmount,
        momGrowthPercentage,
        currentMonthAmount,
        lastMonthAmount,
      };
    },
    enabled: !!jobType && !!convertToINR,
  });
};
